"use client";

import type React from "react";
import { Label } from "@/components/ui/label";
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  Select as SelectUI,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface SelectOption {
  label: string;
  value: string;
}

interface SelectProps {
  options: SelectOption[];
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  label?: string;
  onClear?: () => void;
  disabled?: boolean;
  className?: string;
  emptyValue?: string;
}

export function Select({
  options,
  placeholder,
  value,
  onChange,
  label,
  onClear,
  disabled = false,
  className,
  emptyValue = "",
  ...props
}: SelectProps) {
  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClear?.();
  };

  const isEmpty = !value || value === emptyValue;
  const displayValue = isEmpty ? "" : value;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <Label className="text-sm font-medium text-zinc-950">{label}</Label>
      )}

      <div className="relative">
        <SelectUI
          {...props}
          value={displayValue}
          onValueChange={onChange}
          disabled={disabled}
        >
          <SelectTrigger className={cn("pr-8", className)}>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectUI>

        {!isEmpty && onClear && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-sm transition-colors z-10"
            aria-label="Limpar seleção"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        )}
      </div>
    </div>
  );
}
